/**
 * Program 12.1 UX3 — Tender intake readiness (extends tenderReadiness for Bid/No-Bid gate).
 */
export type TenderIntakeCheck = {
  id: string;
  label: string;
  ok: boolean;
  detail?: string;
};

export type TenderIntakeReadiness = {
  checks: TenderIntakeCheck[];
  ready: boolean;
  blockerCount: number;
  intakeStatus: 'NEEDS_REVIEW' | 'IN_PROGRESS' | 'COMPLETE';
};

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function parseCtx(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  return raw as Record<string, unknown>;
}

export function buildTenderIntakeReadiness(
  tender: Record<string, unknown>,
  opts?: {
    prequalGateOk?: boolean;
    documentCount?: number;
    addendaCount?: number;
  }
): TenderIntakeReadiness {
  const ctx = parseCtx(tender.epc_context_json || tender.epcContextJson);
  const meta = parseCtx(tender.meta_json || tender.metaJson);
  const intake = parseCtx(meta.intake || ctx.intake);

  const checks: TenderIntakeCheck[] = [
    {
      id: 'identity',
      label: 'Tender identity',
      ok: Boolean(asStr(tender.tender_number || tender.tenderNumber)),
    },
    {
      id: 'customer',
      label: 'Customer',
      ok: Boolean(asStr(tender.client_name || tender.clientName)),
    },
    {
      id: 'scope',
      label: 'Scope',
      ok: Boolean(asStr(ctx.scope_summary || intake.scope_summary)),
    },
    {
      id: 'deadline',
      label: 'Submission deadline',
      ok: Boolean(tender.submission_due_at || tender.submissionDueAt),
    },
    {
      id: 'documents',
      label: 'Tender documents',
      ok: (opts?.documentCount ?? 0) > 0 || intake.documents_received === true,
      detail:
        opts?.documentCount != null ? `${opts.documentCount} received` : undefined,
    },
    {
      id: 'qualification',
      label: 'Qualification',
      ok: opts?.prequalGateOk !== false,
    },
    {
      id: 'commercial',
      label: 'Commercial conditions',
      ok: Boolean(ctx.commercial_particulars || intake.commercial_reviewed),
    },
    {
      id: 'risks',
      label: 'Key risks',
      ok: Boolean(intake.risk_reviewed || ctx.risk_reviewed),
    },
    {
      id: 'submission',
      label: 'Submission instructions',
      ok: Boolean(ctx.submission_method || intake.submission_method),
    },
    {
      id: 'owner',
      label: 'Internal owner',
      ok: Boolean(tender.created_by || tender.updated_by),
    },
  ];

  const blockerCount = checks.filter((c) => !c.ok).length;
  const ready = blockerCount === 0;
  let intakeStatus: TenderIntakeReadiness['intakeStatus'] = 'NEEDS_REVIEW';
  if (ready) intakeStatus = 'COMPLETE';
  else if (checks.filter((c) => c.ok).length >= 5) intakeStatus = 'IN_PROGRESS';

  if ((opts?.addendaCount ?? 0) > 0 && !intake.addenda_reviewed) {
    checks.push({
      id: 'addenda',
      label: 'Addenda reviewed',
      ok: false,
      detail: `${opts!.addendaCount} addendum(s) pending review`,
    });
  }

  return {
    checks,
    ready: checks.every((c) => c.ok),
    blockerCount: checks.filter((c) => !c.ok).length,
    intakeStatus,
  };
}

export function daysUntilDue(raw: unknown): number | null {
  if (!raw) return null;
  const d = new Date(String(raw));
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.ceil((due.getTime() - today.getTime()) / (86400000));
}
